import {
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  Box,
} from "@mui/material";
import MDAlert from "components/MDAlert";
import OpportunityForm from "components/opportunities/OpportunityForm/OpportunityForm";
import OpportunityPageFormLoader from "components/loaders/OpportunityPageFormLoader";
import {
  CustomGetOpportunityQuery,
  UpdateOpportunityInput,
  useCustomGetOpportunityLazyQuery,
} from "generated/graphql";
import useUpdateOpportunity from "hooks/opportunities/useUpdateOpportunity";
import { useCallback, useEffect } from "react";

type OpportunityForUpdate = NonNullable<CustomGetOpportunityQuery["getOpportunity"]>;

interface OpportunityEditModalProps {
  open: boolean;
  opportunityId: string | null;
  onClose: () => void;
}

export default function OpportunityEditModal({
  open,
  opportunityId,
  onClose,
}: OpportunityEditModalProps) {
  const [getOpportunity, { loading, error, data }] = useCustomGetOpportunityLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [updateOpportunity, { loading: updating, isSuccess, getData }] = useUpdateOpportunity();

  useEffect(() => {
    if (open && opportunityId) {
      getOpportunity({ variables: { id: opportunityId } });
    }
  }, [open, opportunityId, getOpportunity]);

  const opportunity = (data?.getOpportunity ?? null) as OpportunityForUpdate | null;

  const handleSubmit = useCallback(
    async (input: UpdateOpportunityInput) => {
      const result = await updateOpportunity({ variables: { input } });
      const success = isSuccess(result.data);
      const updated = getData(result);
      return { success, data: updated };
    },
    [updateOpportunity, isSuccess, getData]
  );

  const handleSuccess = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pr: 6 }}>
        {opportunity?.name ? `Edit \"${opportunity.name}\"` : "Edit Opportunity"}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 16, top: 12 }}
        >
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {loading && <OpportunityPageFormLoader />}
        {!loading && error && <MDAlert color="error">{error.message}</MDAlert>}
        {!loading && opportunity && (
          <Box sx={{ backgroundColor: "#fff", borderRadius: 2 }}>
            <OpportunityForm
              action="upsert"
              loading={updating}
              onSubmit={handleSubmit}
              onSuccess={handleSuccess}
              opportunity={opportunity}
              compact
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
